import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Download, LogOut, Save, AlertCircle, CheckCircle2,
  Edit2, Trash2, ChevronDown, ChevronRight, ArrowLeft, Folder, FolderOpen, Link2
} from "lucide-react";
import { adminApi, clearAdminToken, isAdminAuthenticated, formatApiError } from "../lib/adminAuth";

/* -------------------- inline placeholder input -------------------- */
const ALIGN_CLASS = {
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
  left: "text-left",
};

function PlaceholderInput({ field, value, onChange }) {
  // Backend sends `type` on preview.fields and `input_type` on inline segments.
  // Accept either so long-form fields (scope, fees, payment terms) render as a real textarea.
  const inputType = field.input_type || field.type || "text";
  const isTextarea = inputType === "textarea";

  // Placeholder inputs render in Garamond (matches the .docx output formatting)
  const baseClasses =
    "inline-block px-2 py-0.5 border-b-2 border-btacc-gold/50 bg-btacc-gold/10 " +
    "rounded-sm text-btacc-blue text-[15px] align-baseline focus:outline-none " +
    "focus:border-btacc-gold focus:bg-btacc-gold/20 transition-colors mx-0.5";

  const garamondStyle = { fontFamily: 'Garamond, "EB Garamond", "Times New Roman", serif' };

  // Auto-grow textarea: resize on every keystroke so the box always fits the content.
  const taRef = useRef(null);
  useEffect(() => {
    if (isTextarea && taRef.current) {
      taRef.current.style.height = "auto";
      taRef.current.style.height = `${taRef.current.scrollHeight}px`;
    }
  }, [value, isTextarea]);

  if (isTextarea) {
    return (
      <textarea
        ref={taRef}
        value={value || ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        placeholder={field.label}
        rows={4}
        data-testid={`inline-${field.key}`}
        className={`${baseClasses} block w-full my-2 px-3 py-2 resize-y leading-relaxed`}
        style={{ minHeight: "120px", overflow: "hidden", ...garamondStyle }}
      />
    );
  }

  // Single-line input: auto-grow width based on value length, with a sensible min/max.
  const len = Math.max((value || "").length, (field.label || "").length, 12);
  const widthCh = Math.min(Math.max(len + 2, 14), 60);
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(field.key, e.target.value)}
      placeholder={field.label}
      data-testid={`inline-${field.key}`}
      className={baseClasses}
      style={{ width: `${widthCh}ch`, ...garamondStyle }}
    />
  );
}

/* -------------------- contract document view -------------------- */
function ContractDocument({ paragraphs, fields, values, onChange }) {
  // Build a quick lookup from key -> field (so PlaceholderInput knows the input type)
  const fieldByKey = useMemo(() => {
    const m = {};
    for (const f of fields) m[f.key] = f;
    return m;
  }, [fields]);

  return (
    <div
      className="bg-white rounded-lg shadow-lg mx-auto text-btacc-graphite"
      data-testid="contract-document"
      style={{
        fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
        fontSize: "11pt",
        // US Letter aspect (8.5in × 11in) — approximate at 96dpi: 816px width
        maxWidth: "816px",
        padding: "1in",
      }}
    >
      {/* Header: BTACC logo upper-left (matches the .docx header) */}
      <div className="mb-6">
        <img
          src="/images/btacc_logo.png"
          alt="BTACC"
          style={{ width: "2.0in", height: "auto", display: "block" }}
        />
      </div>

      {paragraphs.map((p, i) => {
        const empty = !p.segments || p.segments.every((s) => s.type === "text" && !s.value.trim());
        if (empty) return <div key={i} className="h-3" />;

        const align = ALIGN_CLASS[p.align] || "text-left";
        const isHeading = p.style === "heading";

        // Uniform indentation per nesting level (ignores docx's inconsistent ind values).
        // Every list item at the same level gets the SAME visual position from the left,
        // regardless of which section it belongs to.
        const marker = p.list_marker || null;
        const level = p.list_level || 0;

        // Indent scheme:
        //   - Level 0: marker at the left margin, body text 32px from left
        //   - Level 1: marker at 24px from left, body text 56px from left
        //   - Level 2+: +24px per deeper level
        const markerWidth = 32;
        const baseLeftPerLevel = 24;
        const bodyLeft = markerWidth + level * baseLeftPerLevel;

        const pStyle = {
          lineHeight: 1.5,
          fontSize: isHeading ? "12pt" : "11pt",
        };
        if (marker) {
          pStyle.paddingLeft = `${bodyLeft}px`;
          pStyle.textIndent = `-${markerWidth}px`;
        }

        return (
          <p
            key={i}
            className={`mb-3 ${align} ${isHeading ? "font-bold text-btacc-blue my-4" : ""}`}
            style={pStyle}
          >
            {marker && <span style={{ display: "inline-block", width: `${markerWidth}px`, fontWeight: "inherit" }}>{marker}</span>}
            {p.segments.map((seg, j) => {
              if (seg.type === "placeholder") {
                const f = fieldByKey[seg.key] || { key: seg.key, label: seg.label, input_type: seg.input_type };
                return (
                  <PlaceholderInput
                    key={`${i}-${j}`}
                    field={f}
                    value={values[seg.key]}
                    onChange={onChange}
                  />
                );
              }
            // Text segment — preserve per-run bold/italic from the original .docx.
            // Replace literal tab characters with non-breaking spaces so they render as a
            // visible gap (matches Word's tab rendering).
            const renderedValue = (seg.value || "").replace(/\t/g, "\u00a0\u00a0\u00a0\u00a0");
            let node = <span key={`${i}-${j}`}>{renderedValue}</span>;
            if (seg.italic) node = <em key={`${i}-${j}`} style={{ fontStyle: "italic" }}>{renderedValue}</em>;
            if (seg.bold) node = <strong key={`${i}-${j}`} style={{ fontWeight: 700 }}>{seg.italic ? <em>{renderedValue}</em> : renderedValue}</strong>;
            return node;
            })}
          </p>
        );
      })}
    </div>
  );
}

/* -------------------- main page -------------------- */
export default function AdminContracts() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Section selector: "templates" or "saved"
  const [section, setSection] = useState("templates");
  // Saved-contracts list (all entries)
  const [savedList, setSavedList] = useState([]);
  // ID of the entry currently being edited (so Save Draft updates it instead of creating new)
  const [editingId, setEditingId] = useState(null);
  // Accordion state for the Saved view
  const [openClient, setOpenClient] = useState(null);
  const [savedSearch, setSavedSearch] = useState("");

  const updateValue = useCallback((key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
  }, []);

  const refreshSaved = useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/contracts/saved");
      setSavedList(data || []);
    } catch (err) {
      // Background refresh — don't disrupt the user, but log for diagnostics.
      console.warn("Background refresh of saved contracts failed:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok = await isAdminAuthenticated();
      if (!ok) { navigate("/admin", { replace: true }); return; }
      try {
        const [tplRes, savedRes] = await Promise.all([
          adminApi().get("/admin/contracts/templates"),
          adminApi().get("/admin/contracts/saved"),
        ]);
        if (!mounted) return;
        setTemplates(tplRes.data);
        setActiveId(tplRes.data?.[0]?.id || null);
        setSavedList(savedRes.data || []);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  // Load preview whenever active template changes
  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    (async () => {
      setPreviewLoading(true);
      setError("");
      setSuccess("");
      // Only reset values when NOT loading an existing entry into the editor
      // (the editingId guard: if it was set right before changing activeId, we keep values)
      try {
        const { data } = await adminApi().get(`/admin/contracts/templates/${activeId}/preview`);
        if (!mounted) return;
        setPreview(data);
      } catch (err) {
        if (mounted) setError(formatApiError(err));
      } finally {
        if (mounted) setPreviewLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeId]);

  const logout = () => { clearAdminToken(); navigate("/admin", { replace: true }); };

  const switchToTemplateTab = (tid) => {
    if (tid !== activeId) {
      setActiveId(tid);
    }
  };

  const startNewContract = (tid) => {
    setEditingId(null);
    setValues({});
    setSection("templates");
    switchToTemplateTab(tid);
  };

  /**
   * Switch the active template tab WITHOUT clearing field values. This lets the
   * user fill out one contract (e.g. NDA) and then immediately see the same
   * client data already in place when switching to MSA or SOW. Common fields
   * (client name, address, rep, etc.) are keyed identically across templates,
   * so they just "appear" filled in the other template's view.
   *
   * If we were editing a saved draft, clear `editingId` so the next save
   * creates a NEW entry for the freshly-switched template rather than
   * overwriting the previous one.
   */
  const switchTemplate = (tid) => {
    if (tid === activeId) return;
    setEditingId(null);
    setSection("templates");
    switchToTemplateTab(tid);
  };

  const loadSavedIntoEditor = async (entryId) => {
    setError("");
    setSuccess("");
    try {
      const { data } = await adminApi().get(`/admin/contracts/saved/${entryId}`);
      setEditingId(data.id);
      setValues(data.fields || {});
      setSection("templates");
      switchToTemplateTab(data.template_id);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  /**
   * Create a brand-new contract pre-filled with the client data from any existing
   * saved entry of the same client (any template). The user keeps the source
   * entry untouched and only needs to fill in the fields that are specific to
   * the chosen target template (dates, scope, fees, etc.).
   *
   * Fields auto-copied across all 3 templates:
   *   client_corporate_name, client_name, client_state_country, client_address,
   *   client_rep_name, client_rep_position, btacc_rep_position
   *
   * Special mapping: when target is `msa-sow`, the source's `effective_date`
   * (MSA's signing date) becomes the SOW's `msa_date` reference.
   */
  const createForClient = async (sourceEntry, targetTemplateId) => {
    setError("");
    setSuccess("");
    try {
      const { data } = await adminApi().get(`/admin/contracts/saved/${sourceEntry.id}`);
      const src = data.fields || {};
      const COMMON_KEYS = [
        "client_corporate_name",
        "client_name",
        "client_state_country",
        "client_address",
        "client_rep_name",
        "client_rep_position",
        "btacc_rep_position",
      ];
      const prefilled = {};
      for (const k of COMMON_KEYS) {
        if (src[k]) prefilled[k] = src[k];
      }
      // SOW references the MSA's signing date
      if (targetTemplateId === "msa-sow" && src.effective_date) {
        prefilled.msa_date = src.effective_date;
      }
      setEditingId(null);
      setValues(prefilled);
      setSection("templates");
      switchToTemplateTab(targetTemplateId);
      const labels = { nda: "NDA", msa: "MSA", "msa-sow": "SOW" };
      setSuccess(
        `New ${labels[targetTemplateId] || "contract"} initialized for ${data.client_corporate_name || "this client"}. Fill in the remaining fields.`
      );
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 200);
  };

  const downloadSaved = async (entry) => {
    setError(""); setSuccess("");
    try {
      const response = await adminApi().post(
        `/admin/contracts/saved/${entry.id}/regenerate`,
        {},
        { responseType: "blob" }
      );
      const filename = response.headers["x-filename"] ||
        (response.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] ||
        `BTACC_${entry.template_id}.docx`;
      downloadBlob(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }), filename);
      setSuccess(`Downloaded: ${filename}`);
    } catch (err) {
      let msg = formatApiError(err);
      if (err?.response?.data instanceof Blob) {
        try { msg = (JSON.parse(await err.response.data.text()))?.detail || msg; }
        catch (parseErr) { console.warn("Could not parse error blob:", parseErr); }
      }
      setError(msg);
    }
  };

  const deleteSaved = async (entry) => {
    if (!window.confirm(`Delete "${entry.template_name}" for ${entry.client_corporate_name}?`)) return;
    try {
      await adminApi().delete(`/admin/contracts/saved/${entry.id}`);
      setSavedList((list) => list.filter((x) => x.id !== entry.id));
      if (editingId === entry.id) setEditingId(null);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const saveDraft = async () => {
    if (!preview) return;
    setSavingDraft(true);
    setError(""); setSuccess("");
    try {
      const { data } = await adminApi().post("/admin/contracts/save-draft", {
        template_id: preview.id,
        fields: values,
        id: editingId || undefined,
      });
      setEditingId(data.id);
      setSuccess(`Draft saved for ${data.client_corporate_name || "this contract"}.`);
      refreshSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingDraft(false);
    }
  };

  const generate = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    // Front-end required check (matches backend)
    const missing = preview.fields.filter((f) => f.required && !(values[f.key] || "").trim());
    if (missing.length) {
      setError(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      setSubmitting(false);
      return;
    }
    try {
      const response = await adminApi().post(
        "/admin/contracts/generate",
        { template_id: preview.id, fields: values },
        { responseType: "blob" }
      );
      const filename =
        response.headers["x-filename"] ||
        (response.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] ||
        `BTACC_${preview.id}.docx`;

      // Trigger the download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(url);
      }, 200);

      setSuccess(`Downloaded: ${filename}`);
      refreshSaved();
      // Once generated, this contract is no longer a "draft in progress"
      setEditingId(null);
    } catch (err) {
      let msg = formatApiError(err);
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed?.detail || msg;
        } catch (parseErr) {
          console.warn("Could not parse generate-error blob:", parseErr);
        }
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-btacc-light flex items-center justify-center">
        <p className="font-inter text-btacc-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-contracts-page">
      {/* Top bar */}
      <header className="bg-btacc-blue text-white py-4 px-4 sm:px-6 lg:px-8 shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center" data-testid="admin-brand">
            <h1 className="text-2xl font-montserrat font-bold">
              BT<span className="text-btacc-gold">ACC</span>
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="text-xs font-inter text-btacc-light hover:text-white flex items-center"
          >
            <LogOut size={14} className="mr-1.5" /> Sign out
          </button>
        </div>
      </header>

      {/* Section tabs: Templates vs Saved Contracts */}
      <div className="bg-white border-b border-gray-200 sticky top-[64px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex">
          {[
            { key: "templates", label: "Templates" },
            { key: "saved", label: `Saved Contracts${savedList.length ? ` (${savedList.length})` : ""}` },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              data-testid={`section-${s.key}`}
              className={`px-5 py-3 text-sm font-montserrat font-semibold border-b-2 transition ${
                section === s.key
                  ? "border-btacc-gold text-btacc-blue"
                  : "border-transparent text-btacc-secondary hover:text-btacc-blue"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template tabs (only visible in Templates section) */}
      {section === "templates" && (
        <div className="bg-white border-b border-gray-200 sticky top-[112px] z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="flex flex-wrap gap-1" data-testid="template-tabs">
              {templates?.map((t) => (
                <button
                  key={t.id}
                  onClick={() => switchTemplate(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2.5 text-sm font-montserrat font-semibold border-b-2 transition ${
                    activeId === t.id
                      ? "border-btacc-gold text-btacc-blue"
                      : "border-transparent text-btacc-secondary hover:text-btacc-blue"
                  }`}
                >
                  <FileText size={14} className="inline mr-1.5 -mt-0.5" /> {t.name}
                </button>
              ))}
            </div>
            {editingId && (
              <div className="flex items-center text-xs font-inter text-btacc-blue bg-btacc-gold/15 px-3 py-1.5 rounded">
                <Edit2 size={12} className="mr-1.5" />
                Editing saved draft
                <button
                  className="ml-2 underline text-btacc-secondary hover:text-btacc-blue"
                  onClick={() => { setEditingId(null); setValues({}); }}
                >
                  start fresh
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Alert messages */}
        {error && (
          <div data-testid="admin-error" className="mb-4 max-w-4xl mx-auto bg-red-50 border border-red-200 text-red-800 text-sm font-inter rounded-md p-3 flex items-start">
            <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div data-testid="admin-success" className="mb-4 max-w-4xl mx-auto bg-green-50 border border-green-200 text-green-800 text-sm font-inter rounded-md p-3 flex items-start">
            <CheckCircle2 size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {section === "templates" && (
          <>
            <div className="mb-4 max-w-4xl mx-auto">
              <p className="text-sm font-inter text-btacc-secondary">
                Fill the highlighted fields directly in the document below. Use <strong>Save Draft</strong> to keep your progress without downloading, or <strong>Generate &amp; Download</strong> to produce the .docx.
              </p>
            </div>

            {previewLoading ? (
              <div className="bg-white rounded-lg shadow p-12 max-w-4xl mx-auto text-center text-btacc-secondary font-inter">
                Loading template…
              </div>
            ) : preview ? (
              <ContractDocument
                paragraphs={preview.paragraphs}
                fields={preview.fields}
                values={values}
                onChange={updateValue}
              />
            ) : null}
          </>
        )}

        {section === "saved" && (
          <SavedContractsView
            saved={savedList}
            search={savedSearch}
            onSearchChange={setSavedSearch}
            openClient={openClient}
            onToggleClient={setOpenClient}
            onEdit={loadSavedIntoEditor}
            onDownload={downloadSaved}
            onDelete={deleteSaved}
            onCreateForClient={createForClient}
            onNew={(tid) => startNewContract(tid)}
            templates={templates || []}
          />
        )}
      </main>

      {/* Sticky bottom bar (only in Templates section) */}
      {section === "templates" && preview && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-inter text-btacc-secondary">
              {Object.values(values).filter((v) => (v || "").trim()).length}/{preview.fields.filter((f) => f.required).length} required fields completed
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveDraft}
                disabled={savingDraft}
                data-testid="save-draft-btn"
                className="bg-white border border-btacc-blue text-btacc-blue px-5 py-2.5 rounded-md font-montserrat font-bold text-sm hover:bg-btacc-blue hover:text-white transition flex items-center disabled:opacity-60"
              >
                <Save size={14} className="mr-2" />
                {savingDraft ? "Saving…" : (editingId ? "Update Draft" : "Save Draft")}
              </button>
              <button
                onClick={generate}
                disabled={submitting}
                data-testid="generate-download-btn"
                className="bg-btacc-gold text-btacc-blue px-6 py-2.5 rounded-md font-montserrat font-bold text-sm hover:bg-btacc-blue hover:text-white transition flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download size={16} className="mr-2" />
                {submitting ? "Generating…" : "Generate & Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== Saved Contracts View ====================== */
function SavedContractsView({
  saved, search, onSearchChange,
  openClient, onToggleClient,
  onEdit, onDownload, onDelete, onCreateForClient, onNew, templates,
}) {
  // Group by client_corporate_name
  const groups = useMemo(() => {
    const map = new Map();
    for (const item of saved) {
      const key = (item.client_corporate_name || "(no client name)").trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    // Sort each group by date desc, and groups by their most-recent entry desc
    const arr = Array.from(map.entries()).map(([client, items]) => {
      items.sort((a, b) => new Date(b.generated_at) - new Date(a.generated_at));
      return { client, items, latest: items[0]?.generated_at };
    });
    arr.sort((a, b) => new Date(b.latest) - new Date(a.latest));
    return arr;
  }, [saved]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      g.client.toLowerCase().includes(q) ||
      g.items.some((it) => (it.template_name || "").toLowerCase().includes(q))
    );
  }, [groups, search]);

  return (
    <div className="max-w-5xl mx-auto" data-testid="saved-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-montserrat font-bold text-btacc-blue">Saved Contracts</h2>
          <p className="text-xs font-inter text-btacc-secondary mt-1">
            Drafts and generated contracts, grouped by client. Click <strong>Edit</strong> to keep working on one, or <strong>Download</strong> to grab the .docx.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search client or document…"
            data-testid="saved-search"
            className="px-3 py-2 border border-gray-300 rounded-md font-inter text-sm focus:outline-none focus:ring-2 focus:ring-btacc-gold"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-10 text-center text-sm font-inter text-btacc-secondary">
          {saved.length === 0
            ? "No saved contracts yet. Start filling a template — your work appears here."
            : "No matches for your search."}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => onNew(t.id)}
                className="px-3 py-1.5 bg-btacc-blue text-white rounded text-xs font-montserrat font-semibold hover:bg-btacc-secondary"
              >
                + New {t.name.replace(/\s*\(.*?\)\s*$/, "")}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="saved-groups">
          {filtered.map(({ client, items }) => {
            const isOpen = openClient === client;
            // Pick the best source for cross-template pre-fill. For SOW we
            // prefer the most recent MSA (so `effective_date` maps to msa_date);
            // for everything else, the most recent entry overall.
            const pickSource = (targetId) => {
              if (targetId === "msa-sow") {
                const msa = items.find((i) => i.template_id === "msa");
                if (msa) return msa;
              }
              return items[0];
            };
            return (
              <div key={client} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => onToggleClient(isOpen ? null : client)}
                    className="flex items-center min-w-0 flex-1 text-left"
                    data-testid={`client-group-${client}`}
                  >
                    {isOpen ? <FolderOpen size={16} className="text-btacc-gold mr-2 flex-shrink-0" /> : <Folder size={16} className="text-btacc-secondary mr-2 flex-shrink-0" />}
                    <span className="font-montserrat font-semibold text-btacc-blue truncate">{client}</span>
                    <span className="ml-3 text-xs font-inter text-btacc-secondary whitespace-nowrap">
                      · {items.length} {items.length === 1 ? "contract" : "contracts"}
                    </span>
                  </button>

                  {/* Quick-create: spawn a new contract pre-filled with this client's data */}
                  <div className="flex items-center gap-1 ml-3" data-testid={`quick-create-${client}`}>
                    {templates.map((t) => {
                      const short = { nda: "NDA", msa: "MSA", "msa-sow": "SOW" }[t.id] || t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onCreateForClient(pickSource(t.id), t.id); }}
                          title={`Create a new ${short} pre-filled with ${client}'s data`}
                          data-testid={`btn-quick-${t.id}-${client}`}
                          className="inline-flex items-center px-2.5 py-1 text-[11px] font-montserrat font-semibold text-emerald-700 hover:bg-emerald-600 hover:text-white border border-emerald-300 rounded transition"
                        >
                          + {short}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => onToggleClient(isOpen ? null : client)}
                      className="ml-1 p-1 text-btacc-secondary hover:text-btacc-blue"
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <ul className="border-t border-gray-100 divide-y divide-gray-50">
                    {items.map((it) => (
                      <li key={it.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`saved-item-${it.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-btacc-secondary" />
                            <span className="font-montserrat font-semibold text-sm text-btacc-blue">{it.template_name}</span>
                            {it.is_draft && (
                              <span className="text-[10px] uppercase tracking-wider font-montserrat font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Draft</span>
                            )}
                          </div>
                          <div className="text-xs font-inter text-btacc-secondary mt-0.5">
                            {new Date(it.generated_at).toLocaleString()} · {it.generated_by}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onEdit(it.id)}
                            data-testid={`btn-edit-${it.id}`}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-montserrat font-semibold text-btacc-blue hover:bg-btacc-blue hover:text-white border border-btacc-blue rounded transition"
                          >
                            <Edit2 size={12} className="mr-1.5" /> Edit
                          </button>
                          {it.template_id === "msa" && (
                            <button
                              onClick={() => onCreateForClient(it, "msa-sow")}
                              data-testid={`btn-linked-sow-${it.id}`}
                              title="Create a new SOW pre-filled with this MSA's client data"
                              className="inline-flex items-center px-3 py-1.5 text-xs font-montserrat font-semibold text-emerald-700 hover:bg-emerald-600 hover:text-white border border-emerald-600 rounded transition"
                            >
                              <Link2 size={12} className="mr-1.5" /> Linked SOW
                            </button>
                          )}
                          {!it.is_draft && (
                            <button
                              onClick={() => onDownload(it)}
                              data-testid={`btn-download-${it.id}`}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-montserrat font-semibold bg-btacc-gold text-btacc-blue hover:bg-btacc-blue hover:text-white rounded transition"
                            >
                              <Download size={12} className="mr-1.5" /> Download
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(it)}
                            data-testid={`btn-delete-${it.id}`}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
